import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { random, startCase } from 'lodash';

import { trigger, state, style, transition, animate } from '@angular/core';

import { State } from '../core/store/reducers';
import { NextObstacle } from '../core/store/actions/obstacles.actions';
import { Type, Width, Fish, Spam } from '../core/store/actions/classification.actions';
import { ObstacleModel } from '../core/models';
import { CoreoService } from '../core/services';
import { barrierTypes } from '../core/types';

const THRESHOLD = 5;
const AGREEMENT = 0.8;

@Component({
  selector: 'amber-classifier',
  template: require('./amber-classifier.component.html'),
  styles: [require('./amber-classifier.component.scss')],
  animations: [
    trigger('classifierResults', [
      state('visible', style({
        display: 'flex',
        opacity: 1
      })),
      state('hidden', style({
        display: 'none',
        opacity: 0
      })),
      transition('visible => hidden', animate('200ms linear')),
      transition('hidden => visible', animate('200ms linear')),
    ]),
    trigger('classifierSuccess', [
      state('visible', style({
        transform: 'scale(1)'
      })),
      state('hidden', style({
        transform: 'scale(0)'
      })),
      transition('visible => hidden', animate('200ms ease-out')),
      transition('hidden => visible', animate('200ms 400ms ease-in')),
    ])
  ]
})
export class AmberClassifierComponent implements OnInit {

  questions = ['type', 'width', 'fish'];
  question = null;

  imageLoading = true;
  showResults = false;

  viewingImage = false;

  showSpamButton = false;
  showSpamReport = false;

  types = barrierTypes.filter(item => item.slug !== 'other');

  current$: Observable<ObstacleModel>;
  image$: Observable<string>;
  imageFull$: Observable<string>;
  answered$: Observable<boolean>;
  resultsData$: Observable<number[]>;
  resultsLabels$: Observable<string[]>;
  resultsClassification$: Observable<boolean>;

  lastAnswer$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  chartOptions = {
    legend: {
      labels: {
        boxWidth: 12
      }
    },
    plugins: {
      datalabels: {
        color: '#FFF',
        font: {
          weight: 'bold'
        }
      }
    }
  };

  constructor(
    private store: Store<State>,
    private coreo: CoreoService
  ) { }

  ngOnInit() {
    this.current$ = this.store.select(state => state.obstacles.current);
    this.answered$ = this.store.select(state => state.classification.answered);
    this.resultsData$ = this.store.select(state => state.classification.data);
    this.resultsLabels$ = this.store.select(state => state.classification.labels);

    this.resultsClassification$ = this.resultsData$
      .combineLatest(this.resultsLabels$, this.lastAnswer$)
      .map(([data, labels, last]) => {
        const sum = data.reduce((a, b) => a + b, 0);

        // If there's enough results to classify
        if (sum >= THRESHOLD) {
          const agreementQuantity = sum * AGREEMENT;
          const priorAgreementQuantity = (sum - 1) * AGREEMENT;

          // Iterate over possible answers
          for (let i = 0; i < data.length; i++) {
            // For the answer given, if it now passes the agreement and didn't previously, then accept
            if (labels[i] === startCase(last) && data[i] >= agreementQuantity &&
              (data[i] - 1 < priorAgreementQuantity || (sum - 1) < THRESHOLD)) {
              return true;
            }
          }
        }
        return false;
      });

    this.image$ = this.current$
      .filter(item => item !== null)
      .map(item => this.coreo.photoUrl(item.id, 'photo', 500, 1000));
    this.imageFull$ = this.current$
      .filter(item => item !== null)
      .map(item => this.coreo.photoUrl(item.id, 'photo', undefined, 1000));

    this.selectQuestion();
  }

  imageLoaded() {
    this.imageLoading = false;
    this.showSpamButton = true;
  }

  selectQuestion() {
    this.question = this.questions[random(this.questions.length - 1)];
  }

  answerType(type: string) {
    console.log('It\'s a ' + type);
    this.lastAnswer$.next(type);
    this.store.dispatch(new Type(type));
  }

  answerWidth(fullWidth: string) {
    console.log('Covers full width?', fullWidth);
    this.lastAnswer$.next(fullWidth === 'full' ? 'yes' : 'no');
    this.store.dispatch(new Width(fullWidth));
  }

  answerFish(hasFishPass: string) {
    console.log('Can fish pass?', hasFishPass);
    this.lastAnswer$.next(hasFishPass);
    this.store.dispatch(new Fish(hasFishPass));
  }

  showSpamReportMessage() {
    this.showSpamReport = true;
    this.showSpamButton = false;
  }

  report() {
    console.log('Reporting as spam');
    this.store.dispatch(new Spam());
    this.cancelReport();
    this.nextQuestion();
  }

  cancelReport() {
    this.showSpamReport = false;
    this.showSpamButton = true;
  }

  nextQuestion() {
    this.showResults = false;
    this.imageLoading = true;
    this.showSpamButton = false;
    this.selectQuestion();
    this.store.dispatch(new NextObstacle());
  }

  download() {
    location.href = '#app';
  }

  toggleLightbox() {
    this.viewingImage = !this.viewingImage;
  }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/amber-classifier/amber-classifier.component.ts